import { Component, effect, input, OnInit, output, signal } from '@angular/core';

import { TrackEntryComponent } from '../track-entry/track-entry.component';
import { TimeTrack } from 'src/app/entities/time-track/time-track';
import { Project } from 'src/app/entities/project/project';
import { Tag } from 'src/app/entities/tag/tag';
import { TimeTrackRepository } from 'src/app/services/remote-data-service/repositories/time-track-repository/time-track.repository';
import { FiltersService } from 'src/app/services/filters-service/filters.service';
import { catchError } from 'rxjs';
import { SnackbarService } from 'src/app/services/snackbar-service/snackbar.service';
import { User } from 'src/app/entities/user/user';
import { StoreService } from 'src/app/services/store-service/store.service';

/**
 * Component that displays a list of time tracking entries grouped by day
 * with pagination support through a "Load More" button.
 */
@Component({
  selector: 'app-track-list',
  templateUrl: './track-list.component.html',
  styleUrls: ['./track-list.component.scss'],
  imports: [TrackEntryComponent]
})
export class TrackListComponent implements OnInit {
  /** List of time tracking entries to display */
  trackEntries = input<TimeTrack[]>([]);
  /** Available projects for entry editing */
  projects = input<Project[]>([]);
  /** Available tags for entry editing */
  tags = input<Tag[]>([]);

  users = input<User[]>([]);

  showUsers = input(false);

  /** Date formatting options for day display */
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  };

  /** Event emitter for when entries are updated */
  updateEntries = output<TimeTrack[]>();
  /** Signal indicating if more pages are available */
  hasMorePages = signal(true);
  /** Signal tracking the current page number */
  currentPage = signal(1);


  trackEntriesSanitized: TimeTrack[] = [];

  constructor(
    protected timeTrackRep: TimeTrackRepository,
    protected filters: FiltersService,
    protected snackbar: SnackbarService,
    protected store: StoreService
  ) {
    effect(() => {
      this.trackEntriesSanitized = this.trackEntries().filter(track => track.to)
    })
  }

  ngOnInit(): void {
    this.checkExtendedItems()
  }

  checkExtendedItems() {
    const nextPage = this.currentPage() + 1;
    const currentFilters = this.filters.timeTracksFilters().get(window.location.pathname);

    // Merge current filters with next page
    const updatedFilters = {
      ...currentFilters,
      page: nextPage
    };

    this.timeTrackRep.retrieveAll(updatedFilters).subscribe(newTracks => {
      if (newTracks.length > 0) {
        this.hasMorePages.set(true)
      } else {
        this.hasMorePages.set(false);
      }
    });
  }

  loadMore() {

    const nextPage = this.currentPage() + 1;
    const currentFilters = this.filters.timeTracksFilters().get(window.location.pathname);

    // Merge current filters with next page
    const updatedFilters = {
      ...currentFilters,
      page: nextPage
    };

    this.timeTrackRep.retrieveAll(updatedFilters).subscribe(newTracks => {
      const currentTracks = this.trackEntriesSanitized || [];
      const updatedTracks = [...currentTracks, ...newTracks];
      this.updateEntries.emit(updatedTracks);
      this.currentPage.set(nextPage);
    })
  }

  getUniqueDays(): string[] {
    const days = this.trackEntriesSanitized.map(entry => {
      const date = entry.from;
      return new Date(date).toDateString();
    }) || [];

    // Convert to Date objects for sorting
    const uniqueDays = Array.from(new Set(days));
    return uniqueDays.sort((a, b) => {
      return new Date(b).getTime() - new Date(a).getTime();
    });
  }

  getItemsByDay(day: string): TimeTrack[] | undefined {
    return this.trackEntriesSanitized.filter(entry => {
      const date = entry.from;
      return new Date(date).toDateString() === day
    })?.sort((a, b) => {
      return new Date(b.from).getTime() - new Date(a.from).getTime();
    });
  }

  formatDay(day: string): string {
    const date = new Date(day);
    const options: Intl.DateTimeFormatOptions = { weekday: 'long', month: 'long', day: 'numeric' };
    return date.toLocaleDateString(undefined, options);
  }

  deleteItem(entry: TimeTrack): void {
    const tracks = this.trackEntriesSanitized?.filter(e => e.id !== entry.id);
    if (!tracks) {
      return
    }
    this.timeTrackRep.removeTrack(entry).pipe(
      catchError(() => {
        return []
      })
    ).subscribe(() => {
      this.updateEntries.emit(tracks);
      this.store.saveTimeTracks(tracks)
    })
  }

  updateEntry(entry: TimeTrack): void {
    const trackEntries = this.trackEntriesSanitized;

    this.timeTrackRep.updateTrack(entry).subscribe(() => {
      if (trackEntries) {
        const updatedEntries: TimeTrack[] = trackEntries.map(item =>
          item === entry ? entry : item
        );
        this.updateEntries.emit(updatedEntries);
        this.store.saveTimeTracks(updatedEntries)
      }
    });
  }

  duplicateEntry(entry: TimeTrack): void {
    const tracks = this.trackEntriesSanitized.concat(entry);

    this.timeTrackRep.addNewTrack(entry).pipe(
      catchError(() => {
        return []
      })
    ).subscribe(() => {
      this.updateEntries.emit(tracks)
      this.store.saveTimeTracks(tracks)
    });
  }
}
